import { useEffect, useState, useCallback } from "react";
import type { FC } from "react";

import { message, Modal, Switch, Tooltip, Progress, Tag, Space } from "antd";
import {
  InfoCircleOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  DatabaseOutlined,
  BarcodeOutlined,
} from "@ant-design/icons";
import ContainerShowInfo from "./ContainerShowInfo";
import {
  DISPENSARY_PERMISSION,
  getPagePermissions,
} from "../../../security/permissions";
import ContainerInactiveMessage from "./ContainerInactiveMessage";

import "./ContainerGauges.css";
import { ContainerResource, onChangeStatusContainer1 } from "api/generated";
import { useClient } from "hooks/useClient";
import { handleAxiosError } from "common/helpers/generalUtils";
import useLoggedUser from "hooks/useLoggedUser";

const { confirm } = Modal;

interface Props {
  container: ContainerResource;
  onUpdateContainers: () => void;
}

const formatNumber = (value: number): string =>
  Intl.NumberFormat("en", { notation: "standard" }).format(value);

const getLevelStatus = (pct: number) => {
  if (pct >= 70)
    return {
      color: "#10b981",
      status: "success" as const,
      icon: <CheckCircleOutlined />,
    };
  if (pct >= 40)
    return {
      color: "#3b82f6",
      status: "active" as const,
      icon: <InfoCircleOutlined />,
    };
  if (pct >= 15)
    return {
      color: "#f59e0b",
      status: "normal" as const,
      icon: <ExclamationCircleOutlined />,
    };
  return {
    color: "#ef4444",
    status: "exception" as const,
    icon: <WarningOutlined />,
  };
};

const ContainerGauges: FC<Props> = ({ container, onUpdateContainers }) => {
  const [stateContainer, setStateContainer] =
    useState<ContainerResource>(container);
  const [includeInPreviousInventories, setIncludeInPreviousInventories] =
    useState(false);

  const { client } = useClient();

  const { permissions } = useLoggedUser();

  const dispensaryPermissions = getPagePermissions(
    DISPENSARY_PERMISSION,
    permissions,
  );

  useEffect(() => {
    setStateContainer(container);
  }, [container]);

  const volumePct = stateContainer.lastMeasurementPercentage;
  const levelStatus = getLevelStatus(volumePct ?? 0);
  const measureUnit =
    stateContainer.product?.measurementUnit?.measurementDescription ?? "L";

  const handleContainerStatus = useCallback(
    (containerId: string, checked: boolean) => {
      onChangeStatusContainer1({
        client,
        body: {
          status: checked,
          includeInventoryControl: includeInPreviousInventories,
        },
        path: {
          containerId: containerId,
        },
      })
        .then(({ data }) => {
          message.info(`El Contenedor se a actualizado a ${data}`);
        })
        .catch((err) =>
          handleAxiosError(
            err,
            "No se pudo actualizar el estatus del contenedor",
          ),
        )
        .finally(() => onUpdateContainers());
    },
    [includeInPreviousInventories, client],
  );

  const showInactiveConfirm = useCallback(
    (containerId: string, checked: boolean) => {
      confirm({
        title: `¿Desactivar contenedor ${stateContainer.containerKey}?`,
        icon: <ExclamationCircleOutlined style={{ color: "#f59e0b" }} />,
        content: (
          <ContainerInactiveMessage
            onChangeIncludeInPreviousInventories={
              setIncludeInPreviousInventories
            }
          />
        ),
        okText: "Sí, desactivar",
        okType: "danger",
        cancelText: "Cancelar",
        width: 560,
        centered: true,
        onOk: () =>
          new Promise<void>((resolve) => {
            setTimeout(() => {
              handleContainerStatus(containerId, checked);
              resolve();
            }, 1500);
          }),
      });
    },
    [stateContainer.containerKey, handleContainerStatus],
  );

  return (
    <div
      className={`cg-card ${!stateContainer.containerStatus ? "cg-card--inactive" : ""}`}
    >
      <div className="cg-card__header">
        <div className="cg-card__header-left">
          <div className="cg-card__icon-wrapper">
            <DatabaseOutlined className="cg-card__icon" />
          </div>
          <div className="cg-card__title-group">
            <h3 className="cg-card__title">{stateContainer.containerKey}</h3>
            <span className="cg-card__subtitle">
              {stateContainer.containerType === "TANQUE"
                ? "Tanque"
                : stateContainer.containerType === "POZO"
                  ? "Pozo"
                  : "Ducto"}
              {" · "}
              <span className="cg-card__warehouse">
                {stateContainer.warehouseKey}
              </span>
            </span>
          </div>
        </div>

        <div className="cg-card__header-right">
          <Tag
            color={stateContainer.containerStatus ? "green" : "default"}
            className="cg-card__status-tag"
          >
            {stateContainer.containerStatus ? "Activo" : "Inactivo"}
          </Tag>
          {dispensaryPermissions.edit && (
            <Tooltip
              title={
                stateContainer.containerStatus
                  ? "Desactivar contenedor"
                  : "Contenedor inactivo"
              }
            >
              <Switch
                size="small"
                checked={stateContainer.containerStatus}
                disabled={!stateContainer.containerStatus}
                onChange={(checked) =>
                  showInactiveConfirm(stateContainer.idContainer ?? "", checked)
                }
              />
            </Tooltip>
          )}
        </div>
      </div>

      {stateContainer.containerType === "TANQUE" && (
        <div className="cg-card__tank-section">
          <div className="cg-card__tank-visual">
            <div className="cg-tank">
              <div
                className="cg-tank__fill"
                style={{
                  height: `${volumePct}%`,
                  background: `linear-gradient(180deg, ${levelStatus.color}44 0%, ${levelStatus.color} 100%)`,
                }}
              />
              <div className="cg-tank__percentage">{volumePct}%</div>
            </div>
          </div>

          <div className="cg-card__tank-info">
            <div className="cg-card__stat">
              <span className="cg-card__stat-label">Capacidad</span>
              <span className="cg-card__stat-value">
                {formatNumber(stateContainer.containerTotalCapacity ?? 0)}{" "}
                <small>{measureUnit}</small>
              </span>
            </div>
            <div className="cg-card__stat">
              <span className="cg-card__stat-label">Volumen actual</span>
              <span className="cg-card__stat-value">
                {formatNumber(stateContainer.lastMeasurementVolume ?? 0)}{" "}
                <small>{measureUnit}</small>
              </span>
            </div>
            <Progress
              percent={volumePct}
              status={levelStatus.status}
              strokeColor={levelStatus.color}
              size="small"
              className="cg-card__progress"
            />
          </div>
        </div>
      )}

      <div className="cg-card__gauges-summary">
        <Space style={{ width: "100%" }} direction="vertical">
          <span style={{ width: "100%" }}>
            <BarcodeOutlined
              style={{
                marginRight: 6,
                color: "#64748b",
              }}
            />
            Código de integración:{" "}
            {stateContainer.integrationCode ? (
              <Tag
                color="blue"
                style={{
                  marginLeft: 4,
                  textOverflow: "ellipsis",
                  overflow: "hidden",

                  maxWidth: "100%",
                }}
              >
                {stateContainer.integrationCode}
              </Tag>
            ) : (
              <span style={{ color: "#94a3b8", fontStyle: "italic" }}>
                Sin código
              </span>
            )}
          </span>

          <div
            className="cg-card__actions-row"
            style={{ display: "flow", textAlign: "end" }}
          >
            <ContainerShowInfo
              containerId={stateContainer.idContainer ?? ""}
              onDeleteContainers={onUpdateContainers}
            />
          </div>
        </Space>
      </div>
    </div>
  );
};

export default ContainerGauges;
